import { useEffect } from 'react';

const THRESHOLD = 0.5;

function getVisibleWidth(elBoundingRect: DOMRect) {
	if (elBoundingRect.x + elBoundingRect.width >= window.innerWidth) {
		return window.innerWidth - elBoundingRect.x;
	}

	if (elBoundingRect.x + elBoundingRect.width <= 0) {
		return 0;
	}

	return elBoundingRect.width;
}

function getVisibleHeight(elBoundingRect: DOMRect) {
	if (elBoundingRect.y + elBoundingRect.height >= window.innerHeight) {
		return window.innerHeight - elBoundingRect.y;
	}

	if (elBoundingRect.y + elBoundingRect.height <= 0) {
		return 0;
	}

	return elBoundingRect.height;
}

function isVisible(elBoundingRect: DOMRect) {
	if (
		(elBoundingRect.height === 0 && getVisibleWidth(elBoundingRect) > 0) ||
		(elBoundingRect.width === 0 && getVisibleHeight(elBoundingRect) > 0)
	) {
		return true;
	}

	return (
		getVisibleWidth(elBoundingRect) > 0 && getVisibleHeight(elBoundingRect) > 0
	);
}

function getIntersectionRatio(elBoundingRect: DOMRect) {
	if (!isVisible(elBoundingRect)) return 0;

	if (elBoundingRect.height === 0 && getVisibleWidth(elBoundingRect) > 0) {
		return getVisibleWidth(elBoundingRect) / elBoundingRect.width;
	}

	if (elBoundingRect.width === 0 && getVisibleHeight(elBoundingRect) > 0) {
		return getVisibleHeight(elBoundingRect) / elBoundingRect.height;
	}

	return (
		(getVisibleHeight(elBoundingRect) * getVisibleWidth(elBoundingRect)) /
		(elBoundingRect.width * elBoundingRect.height)
	);
}

function getIsInViewport(el: HTMLElement, threshold = 1) {
	const elBoundingRect = el.getBoundingClientRect();

	if (getIntersectionRatio(elBoundingRect) > threshold) {
		return true;
	}

	return false;
}

function checkIsAppearWithScroll(ref: HTMLElement, onAppear = () => {}) {
	if (getIsInViewport(ref, THRESHOLD)) {
		onAppear();
		return () => {};
	}

	const onScroll = () => {
		if (getIsInViewport(ref, THRESHOLD)) {
			onAppear();
			window.removeEventListener('scroll', onScroll);
		}
	};

	window.addEventListener('scroll', onScroll, {
		passive: true,
	});

	return () => {
		window.removeEventListener('scroll', onScroll);
	};
}

function checkIsAppearWithObserver(ref: HTMLElement, onAppear = () => {}) {
	const observer = new IntersectionObserver(
		(entries) => {
			const entry = entries[0];

			if (entry.isIntersecting) {
				onAppear();
				observer.disconnect();
			}
		},
		{ threshold: THRESHOLD }
	);

	observer.observe(ref);

	return () => {
		observer.disconnect();
	};
}

export function useAppear(ref: HTMLElement | null = null, onAppear = () => {}) {
	useEffect(() => {
		if (!ref) return () => {};

		const isIntersectionObserver = 'IntersectionObserver' in window === true;

		const clearListener = isIntersectionObserver
			? checkIsAppearWithObserver(ref, onAppear)
			: checkIsAppearWithScroll(ref, onAppear);

		return clearListener;
	}, [ref]);
}
